import React, { useEffect, useState, useMemo } from "react";
import { Table, Skeleton, Tag, Tooltip } from "antd";
import { Link, useNavigate } from "react-router-dom";
import {
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowRight,
  Search,
  Bell,
  Activity,
  FileCheck,
  Calendar,
} from "lucide-react";
import { onboardingService, OnboardingClient, OnboardingAnalytics } from "../../services/api/onboardingService";
import { toast } from "sonner";
import SearchInput from "../../components/elements/forms/SearchInput";

const OnboardingClientsList: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<OnboardingClient[]>([]);
  const [analytics, setAnalytics] = useState<OnboardingAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await onboardingService.getClients();
        if (response.status === "success" && Array.isArray(response.data)) {
          setClients(response.data);
        } else {
          toast.error(response.message || "Failed to fetch onboarding clients");
        }
      } catch (error) {
        console.error("Error fetching onboarding clients:", error);
        toast.error("An unexpected error occurred while fetching clients");
      } finally {
        setLoading(false);
      }
    };

    const fetchAnalytics = async () => {
      setLoadingAnalytics(true);
      try {
        const response = await onboardingService.getAnalytics();
        if (response.status === "success" && response.data) {
          setAnalytics(response.data);
        }
      } catch (error) {
        console.error("Error fetching onboarding analytics:", error);
      } finally {
        setLoadingAnalytics(false);
      }
    };

    fetchData();
    fetchAnalytics();
  }, []);

  // Filter clients based on search and status filters
  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      const matchesSearch =
        client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.representativeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "ALL" || client.onboardingStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [clients, searchTerm, statusFilter]);

  const getStatusTagColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "success";
      case "AWAITING_INTERNAL_APPROVAL":
        return "processing";
      case "PENDING":
        return "warning";
      case "DISAPPROVED":
        return "error";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "S/N",
      key: "index",
      width: 60,
      align: "center" as const,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Organization Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: OnboardingClient) => (
        <div className="font-medium text-gray-900 hover:text-[#0B60D6] transition-colors cursor-pointer" onClick={() => navigate(`/onboarding-clients/${record.id}`)}>
          {text}
        </div>
      ),
    },
    {
      title: "Representative",
      dataIndex: "representativeName",
      key: "representativeName",
      render: (text: string, record: OnboardingClient) => (
        <div>
          <div className="text-gray-900 font-medium">{text || `${record.representativeFirstName} ${record.representativeLastName}`}</div>
          <div className="text-xs text-gray-500">{record.representativeEmail}</div>
        </div>
      ),
    },
    {
      title: "Onboarding Status",
      dataIndex: "onboardingStatus",
      key: "onboardingStatus",
      render: (status: string) => (
        <Tag color={getStatusTagColor(status)} className="px-2 py-0.5 text-xs font-semibold rounded-full border-none">
          {status?.replace(/_/g, " ")}
        </Tag>
      ),
    },
    {
      title: "Nudges Sent",
      dataIndex: "onboardingNudgeCount",
      key: "onboardingNudgeCount",
      align: "center" as const,
      render: (count: number) => (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${count > 3 ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-700"}`}>
          {count || 0}
        </span>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : "N/A"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center" as const,
      render: (_: any, record: OnboardingClient) => (
        <Link
          to={`/onboarding-clients/${record.id}`}
          className="inline-flex items-center gap-1 text-sm font-semibold text-[#0B60D6] hover:text-[#0948a8] transition-colors"
        >
          View Details
          <ArrowRight size={14} />
        </Link>
      ),
    },
  ];

  return (
    <div className="min-h-screen font-abel bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header & Title */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Client Onboardings</h1>
            <p className="text-sm text-gray-500 mt-1">
              Track progress, approvals, and setup milestones for all client implementations.
            </p>
          </div>
        </div>

        {/* Reusable Analytics Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Total Clients Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5">
            <div className="space-y-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Onboardings</span>
              {loadingAnalytics ? (
                <div className="h-8 w-16 bg-gray-100 animate-pulse rounded"></div>
              ) : (
                <h3 className="text-3xl font-bold text-gray-900">{analytics?.totalClients ?? 0}</h3>
              )}
            </div>
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
              <Users size={24} />
            </div>
          </div>

          {/* Active Onboarding Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5">
            <div className="space-y-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Active Progress</span>
              {loadingAnalytics ? (
                <div className="h-8 w-16 bg-gray-100 animate-pulse rounded"></div>
              ) : (
                <h3 className="text-3xl font-bold text-gray-900">{analytics?.activeOnboardingCount ?? 0}</h3>
              )}
            </div>
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <Clock size={24} />
            </div>
          </div>

          {/* Completed Onboarding Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5">
            <div className="space-y-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Completed Setup</span>
              {loadingAnalytics ? (
                <div className="h-8 w-16 bg-gray-100 animate-pulse rounded"></div>
              ) : (
                <h3 className="text-3xl font-bold text-gray-900">{analytics?.completedOnboardingCount ?? 0}</h3>
              )}
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <CheckCircle size={24} />
            </div>
          </div>

          {/* Pending Approval / Nudges Alert */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5">
            <div className="space-y-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Awaiting Approvals</span>
              {loadingAnalytics ? (
                <div className="h-8 w-16 bg-gray-100 animate-pulse rounded"></div>
              ) : (
                <h3 className="text-3xl font-bold text-gray-900">
                  {analytics?.internalApprovals?.goLivePendingApprovalCount ?? 0}
                </h3>
              )}
            </div>
            <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
              <FileCheck size={24} />
            </div>
          </div>
        </div>

        {/* Detailed Analytics Accordion/Drawer Section (Secondary metrics) */}
        {!loadingAnalytics && analytics && (
          <div className="bg-gradient-to-r from-blue-50/50 via-indigo-50/20 to-white rounded-xl p-5 border border-blue-100/60 flex flex-wrap gap-6 items-center justify-between">
            <div className="flex flex-wrap gap-5 text-sm">
              <div className="flex items-center gap-2">
                <Activity size={16} className="text-indigo-500" />
                <span className="text-gray-500">Stale Clients:</span>
                <span className="font-semibold text-gray-900">{analytics.nudgesAndActivity?.staleOnboardingCount ?? 0}</span>
              </div>
              <div className="flex items-center gap-2 border-l border-gray-200 pl-5">
                <Bell size={16} className="text-amber-500" />
                <span className="text-gray-500">High Nudge Count:</span>
                <span className="font-semibold text-gray-900">{analytics.nudgesAndActivity?.highNudgeCount ?? 0}</span>
              </div>
              <div className="flex items-center gap-2 border-l border-gray-200 pl-5">
                <Calendar size={16} className="text-rose-500" />
                <span className="text-gray-500">Overdue Deadlines:</span>
                <span className="font-semibold text-gray-900">{analytics.deadlines?.overdue ?? 0}</span>
              </div>
            </div>
            <div className="text-xs text-gray-400 font-mono">
              Avg. Completion Days: <span className="font-bold text-gray-600">{analytics.averageCompletionDays || 0} days</span>
            </div>
          </div>
        )}

        {/* Client Table Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <button
                onClick={() => setStatusFilter("ALL")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all ${
                  statusFilter === "ALL"
                    ? "bg-[#0B60D6] text-white shadow-sm"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                }`}
              >
                All Onboardings
              </button>
              <button
                onClick={() => setStatusFilter("AWAITING_INTERNAL_APPROVAL")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all ${
                  statusFilter === "AWAITING_INTERNAL_APPROVAL"
                    ? "bg-[#0B60D6] text-white shadow-sm"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                }`}
              >
                Awaiting Approvals
              </button>
              <button
                onClick={() => setStatusFilter("COMPLETED")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all ${
                  statusFilter === "COMPLETED"
                    ? "bg-[#0B60D6] text-white shadow-sm"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                }`}
              >
                Completed
              </button>
            </div>

            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by client or representative..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0B60D6] focus:border-[#0B60D6] placeholder-gray-400 transition-all"
              />
            </div>
          </div>

          {loading ? (
            <Skeleton active paragraph={{ rows: 8 }} />
          ) : (
            <Table
              dataSource={filteredClients}
              columns={columns}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showTotal: (total) => `Total ${total} onboarding clients`,
                showSizeChanger: false,
              }}
              className="border border-gray-100 rounded-lg overflow-hidden"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingClientsList;
